<template>
  <header class="fixed top-0 left-0 right-0 z-50 bg-white transition-transform duration-300" 
          :class="{ '-translate-y-full': isHidden }">
    <!-- 顶部状态栏 -->
    <div class="flex justify-between items-center px-5 py-3 bg-gray-100 border-b border-gray-300">
      
      <div class="flex-1 text-center">
        <h1 class="text-lg font-semibold text-gray-700 m-0">{{ pageTitle }}</h1>
      </div>
      
    </div>

    <!-- 主导航栏 -->
    <div class="flex justify-between items-center px-4 py-1 bg-white">
      <div class="flex-1">
        <img src="@/assets/simple_logo.jpg" alt="CRE Logo" class="h-9 w-auto" />
      </div>
      
      <div class="flex items-center gap-4">
        <!-- 语言选择器 -->
        <div class="flex items-center gap-2 bg-blue-900 px-3 py-2 rounded-full cursor-pointer" @click="openLanguageDialog">
          <img :src="selectedLang.flag" :alt="`${selectedLang.name} Flag`" class="w-5 h-4 rounded-sm" />
          <span class="text-white text-sm font-semibold">{{ selectedLang.code.toUpperCase() }}</span>
          <svg class="w-4 h-4 text-white" viewBox="0 0 24 24" fill="currentColor">
            <path d="M7 10l5 5 5-5z"/>
          </svg>
        </div>
        
        <!-- 汉堡菜单 -->
        <button class="flex flex-col justify-between w-6 h-5 bg-transparent border-none cursor-pointer p-0" @click="toggleMenu">
          <span class="block h-0.5 w-full bg-blue-900 rounded-sm transition-all duration-300"></span>
          <span class="block h-0.5 w-full bg-blue-900 rounded-sm transition-all duration-300"></span>
          <span class="block h-0.5 w-full bg-blue-900 rounded-sm transition-all duration-300"></span>
        </button>
      </div>
    </div>

    <!-- 移动端菜单覆盖层 -->
    <div v-if="showMobileMenu" class="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 z-[60] flex justify-center items-start pt-0" @click="closeMobileMenu">
      <div class="bg-gray-200 w-screen rounded-b-3xl p-5 shadow-2xl" @click.stop>
        <!-- 关闭按钮 -->
        <div class="flex justify-end mb-5">
          <button class="bg-transparent border-none p-2 cursor-pointer" @click="closeMobileMenu">
            <svg class="w-6 h-6 text-gray-700" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        
        <!-- 菜单项 -->
        <nav class="flex flex-col gap-2">
          <router-link 
            to="/" 
            class="block py-4 text-base font-semibold no-underline border-b border-gray-300"
            :class="isActiveRoute('/') ? 'text-blue-900 font-bold' : 'text-gray-700'"
            @click="handleMenuClick('/')"
          >
            {{ menuTexts.home }}
          </router-link>
          
          <router-link 
            to="/about" 
            class="block py-4 text-base font-semibold no-underline border-b border-gray-300"
            :class="isActiveRoute('/about') ? 'text-blue-900 font-bold' : 'text-gray-700'"
            @click="handleMenuClick('/about')"
          >
            {{ menuTexts.aboutUs }}
          </router-link>
          
          <router-link 
            to="/core-offers" 
            class="block py-4 text-base font-semibold no-underline border-b border-gray-300"
            :class="isActiveRoute('/core-offers') ? 'text-blue-900 font-bold' : 'text-gray-700'"
            @click="handleMenuClick('/core-offers')"
          >
            {{ menuTexts.ourOffers }}
          </router-link>
          
          <router-link 
            to="/core-offers/source-product" 
            class="block py-3 pl-5 text-sm font-medium no-underline"
            :class="isActiveRoute('/core-offers/source-product') ? 'text-blue-900 font-bold' : 'text-gray-700'"
            @click="handleMenuClick('/core-offers/source-product')"
          >
            • {{ menuTexts.sourcingIllustrations }}
          </router-link>
          
          <router-link
            id="cta-contact-header"
            to="/contact-us"
            class="block py-4 text-base font-semibold no-underline"
            :class="isActiveRoute('/contact-us') ? 'text-blue-900 font-bold' : 'text-gray-700'"
            @click="handleContactClick"
          >
            {{ menuTexts.contactUs }}
          </router-link>
        </nav>
      </div>
    </div>

    <!-- 语言选择对话框 -->
    <MobileLanguageDialog
      v-model:visible="showLanguageDialog"
      :languages="languages"
      @select-language="handleSelectLanguage"
    />
  </header>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import MobileLanguageDialog from '@/components/MobileLanguageDialog.vue'
import { languages } from '@/utils/languages'
import { saveCTAInfo, CTA_CONFIG } from '../utils/ctaTracking'

// 滚动控制
const isHidden = ref(false)
const lastScrollY = ref(0)

const handleScroll = () => {
  const currentScrollY = window.scrollY
  
  // 向下滚动且不在顶部时隐藏
  if (currentScrollY > lastScrollY.value && currentScrollY > 100) {
    isHidden.value = true
  } 
  // 向上滚动时显示
  else if (currentScrollY < lastScrollY.value) {
    isHidden.value = false
  }
  
  lastScrollY.value = currentScrollY
}

onMounted(() => {
  window.addEventListener('scroll', handleScroll, { passive: true })
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
})

const { locale, messages } = useI18n()
const route = useRoute()
const router = useRouter()

// 添加调试函数
const isActiveRoute = (path) => {
  console.log('Current route:', route.path, 'Checking path:', path)
  if (path === '/') {
    return route.path === '/' || route.path === ''
  }
  if (path === '/core-offers') {
    return route.path === '/core-offers' || route.path.startsWith('/core-offers/')
  }
  return route.path === path
}

// Props
const props = defineProps({
  pageTitle: {
    type: String,
    default: 'About Us'
  }
})

// 获取菜单文本
const menuTexts = computed(() => {
  return messages.value[locale.value]?.menu || {
    home: 'Home',
    aboutUs: 'About Us',
    ourOffers: 'Our Offers',
    contactUs: 'Contact Us',
    sourcingIllustrations: 'Sourcing Illustrations'
  };
});

const selectedLang = ref(
  languages.find(l => l.code === locale.value) || languages[0]
)

const showLanguageDialog = ref(false)
const showMobileMenu = ref(false)

const openLanguageDialog = () => {
  showLanguageDialog.value = true
}

const handleSelectLanguage = (lang) => {
  const oldLang = locale.value
  locale.value = lang.code
  selectedLang.value = lang
  
  localStorage.setItem('language', lang.code)
  localStorage.setItem('language_manual_selection', 'true')
  localStorage.setItem('language_selection_date', new Date().toISOString())
}

// 菜单控制
const toggleMenu = () => {
  showMobileMenu.value = !showMobileMenu.value
}

const closeMobileMenu = () => {
  showMobileMenu.value = false
}

const handleMenuClick = (path) => {
  closeMobileMenu()
  router.push(path)
}

const handleContactClick = () => {
  // 保存 CTA 信息到 sessionStorage
  saveCTAInfo(CTA_CONFIG.HEADER_CONTACT)
  closeMobileMenu()
  router.push('/contact-us')
}
</script>

<style scoped>
/* 移除所有自定义CSS，完全使用Tailwind类 */
</style>
